import { useEffect, useRef, useState } from "react";
import { ActiveFilters, FilterCriteria } from "../../types";
import { defaultFilterCriteria } from "../SubmitTip/resources";
import useFetchWithAuth from "./useFetchWithAuth";
import { getApiUrl } from "../../utils/api";
import devLogger from "../../utils/devLogger";

export const useReportFilter = () => {
  const [filtering, setFiltering] = useState(false);
  const { fetchWithAuth } = useFetchWithAuth();

  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    eventConcern: false,
    location: false,
    date: false,
    reporterDetails: false,
    abuserInfo: false,
    supportServices: false,
    status: false,
  });

  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>(
    defaultFilterCriteria
  );

  const prevCriteriaRef = useRef<FilterCriteria>(filterCriteria);

  useEffect(() => {
    prevCriteriaRef.current = filterCriteria;
  }, [filterCriteria]);

  const handleFilterChange = (selectedOptions: any) => {
    const updatedFilters = { ...activeFilters };

    Object.keys(updatedFilters).forEach((key) => {
      updatedFilters[key as keyof ActiveFilters] = false;
    });

    // Enable the filters selected in the multi-select
    selectedOptions.forEach((option: { value: string }) => {
      updatedFilters[option.value as keyof ActiveFilters] = true;
    });

    setActiveFilters(updatedFilters);
  };

  const handleFilter = async () => {
    // Create a params object instead of using URLSearchParams
    const params: Record<string, string | number | boolean> = {};

    // Add query parameters based on filterCriteria
    if (filterCriteria.caseType) params["caseName"] = filterCriteria.caseType;

    if (filterCriteria.subCaseType)
      params["caseType"] = filterCriteria.subCaseType;

    if (filterCriteria.date.type !== "all") {
      params["mode"] = filterCriteria.date.mode;

      if (filterCriteria.date.startDate)
        params["startDate"] = filterCriteria.date.startDate;

      if (filterCriteria.date.endDate)
        params["endDate"] = filterCriteria.date.endDate;
    }

    if (filterCriteria.supportServices.NeedAnAttorney)
      params["NeedAnAttorney"] = filterCriteria.supportServices.NeedAnAttorney;

    if (filterCriteria.supportServices.NeedCounseling)
      params["NeedCounseling"] = filterCriteria.supportServices.NeedCounseling;

    if (filterCriteria.supportServices.NeedPrivateInvestigator)
      params["NeedPrivateInvestigator"] =
        filterCriteria.supportServices.NeedPrivateInvestigator;

    if (filterCriteria.location) {
      params["incidentDescription.location"] = filterCriteria.location;
    }

    if (filterCriteria.detailedIncidentLocation.state)
      params["incidentDescription.detailedLocation.state"] =
        filterCriteria.detailedIncidentLocation.state;

    if (filterCriteria.detailedIncidentLocation.city)
      params["incidentDescription.detailedLocation.city"] =
        filterCriteria.detailedIncidentLocation.city;

    if (filterCriteria.detailedIncidentLocation.county)
      params["incidentDescription.detailedLocation.county"] =
        filterCriteria.detailedIncidentLocation.county;

    // Reporter Details
    if (filterCriteria.reporterDetails.role)
      params["reporterDetails.role"] = filterCriteria.reporterDetails.role;

    if (filterCriteria.reporterDetails.gender)
      params["reporterDetails.gender"] = filterCriteria.reporterDetails.gender;

    if (filterCriteria.reporterDetails.age)
      params["reporterDetails.age"] = filterCriteria.reporterDetails.age;

    if (filterCriteria.reporterDetails.phone)
      params["reporterDetails.phone"] = filterCriteria.reporterDetails.phone;

    if (filterCriteria.reporterDetails.email)
      params["reporterDetails.email"] = filterCriteria.reporterDetails.email;

    if (filterCriteria.reporterDetails.ethnicity)
      params["reporterDetails.ethnicity"] =
        filterCriteria.reporterDetails.ethnicity;

    // Abuser Details
    if (filterCriteria.abuserInfo.name)
      params["abuserDetails.name"] = filterCriteria.abuserInfo.name;

    if (filterCriteria.abuserInfo.alias)
      params["abuserDetails.name"] = filterCriteria.abuserInfo.alias;

    if (filterCriteria.abuserInfo.gender)
      params["abuserDetails.gender"] = filterCriteria.abuserInfo.gender;

    if (filterCriteria.abuserInfo.refAge)
      params["abuserDetails.refAge"] = filterCriteria.abuserInfo.refAge;

    if (filterCriteria.abuserInfo.relation)
      params["abuserDetails.relation"] = filterCriteria.abuserInfo.relation;

    if (filterCriteria.abuserInfo.occupation)
      params["abuserDetails.occupation"] = filterCriteria.abuserInfo.occupation;

    if (filterCriteria.abuserInfo.ethnicity)
      params["abuserDetails.ethnicity"] = filterCriteria.abuserInfo.ethnicity;

    if (filterCriteria.abuserInfo.hasSpecialAuthority)
      params["abuserDetails.hasSpecialAuthority"] =
        filterCriteria.abuserInfo.hasSpecialAuthority;

    if (filterCriteria.abuserInfo.authorityPosition)
      params["abuserDetails.specialAuthorityPosition"] =
        filterCriteria.abuserInfo.authorityPosition;

    if (filterCriteria.abuserInfo.phyDescription)
      params["abuserDetails.phyDescription"] =
        filterCriteria.abuserInfo.phyDescription;

    if (filterCriteria.abuserInfo.hasAbuserVehicleInfo) {
      const vehicle = filterCriteria.abuserInfo.vehicleInfo;
      if (vehicle?.brand) params["abuserInfo.brand"] = vehicle.brand;
      if (vehicle?.model) params["abuserInfo.model"] = vehicle.model;
      if (vehicle?.carColor) params["abuserInfo.color"] = vehicle.carColor;
      if (vehicle?.licensePlate)
        params["abuserInfo.licensePlate"] = vehicle.licensePlate;
    }

    if (filterCriteria.reportStatus)
      params["status"] = filterCriteria.reportStatus;

    // Use the enhanced getApiUrl with the params object
    const url = getApiUrl("/admin/reports", params);

    try {
      setFiltering(true);
      const response = await fetchWithAuth(url);
      return response;
    } catch (error) {
      devLogger.error("Error fetching data:", error);
      return null;
    } finally {
      setFiltering(false);
    }
  };

  return {
    activeFilters,
    filterCriteria,
    filtering,
    setFilterCriteria,
    handleFilterChange,
    handleFilter,
    setFiltering,
  };
};
